import React from 'react';
// CommercialGate D.1 - sync marker
import { Loader2, AlertCircle, LogOut, RefreshCw, Building2, Lock } from 'lucide-react';
import { useEntitlement } from '../utils/entitlement';
import { useRvLogo } from '../utils/useRvLogo';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const EXPIRED_STATUS_LABELS: Record<string, string> = {
  trial_expired: 'תקופת הניסיון הסתיימה',
  past_due: 'המנוי אינו פעיל — תשלום לא התקבל',
  cancelled: 'המנוי בוטל',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'בעל משרד',
  manager: 'מנהל',
  agent: 'סוכן',
};

interface CommercialGateProps {
  children: React.ReactNode;
}

/**
 * Centralized commercial access gate.
 *
 * Routing priority (deterministic):
 * 1. Entitlement loading → loading screen
 * 2. Entitlement error → error/retry screen (fail-closed)
 * 3. No office + not commercially blocked → pass through (onboarding/invitation flow)
 * 4. Has office + can_access_app === false + not owner → commercial blocked screen
 * 5. Has office + can_access_app === true → pass through (optionally with owner expired banner)
 *
 * Owner is NEVER blocked from entering the app, even in expired states.
 */
export default function CommercialGate({ children }: CommercialGateProps) {
  const { entitlement, resolutionState, error, refresh } = useEntitlement();
  const navigate = useNavigate();
  const logoUrl = useRvLogo();

  // 1. Entitlement loading
  if (resolutionState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          {logoUrl ? (
            <img src={logoUrl} alt="Realty Value" className="w-20 h-20 mx-auto mb-4 object-contain" />
          ) : (
            <Building2 size={40} className="mx-auto mb-4 text-blue-400 animate-pulse" />
          )}
          <Loader2 size={28} className="animate-spin text-blue-400 mx-auto mb-3" />
          <p className="text-white text-sm font-medium">טוען את הרשאות החשבון...</p>
        </div>
      </div>
    );
  }

  // 2. Entitlement error (fail-closed)
  if (resolutionState === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4" dir="rtl">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle size={32} className="text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-white mb-2">לא הצלחנו לאמת את הרשאות החשבון</h2>
          <p className="text-sm text-gray-400 mb-5">ייתכן שמדובר בתקלה זמנית. נסה שוב בעוד רגע.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => refresh()}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm font-semibold"
            >
              <RefreshCw size={15} />
              נסה שוב
            </button>
            <button
              onClick={async () => {
                await signOut(auth);
                navigate('/login');
              }}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors text-sm font-semibold"
            >
              <LogOut size={15} />
              התנתקות
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. No office → pass through to onboarding/invitation flow (Sprint C behavior)
  const hasOffice = !!entitlement.office_id;
  if (!hasOffice) {
    return <>{children}</>;
  }

  // 4. Has office + commercially blocked + NOT owner → blocked screen
  const isOwner = entitlement.my_role === 'owner';
  const isBlocked = !entitlement.can_access_app;

  if (isBlocked && !isOwner) {
    const statusLabel = EXPIRED_STATUS_LABELS[entitlement.subscription_status ?? ''] ?? 'המנוי אינו פעיל';
    const roleLabel = ROLE_LABELS[entitlement.my_role ?? ''] ?? entitlement.my_role ?? '';

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4" dir="rtl">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md w-full text-center">
          {logoUrl ? (
            <img src={logoUrl} alt="Realty Value" className="w-16 h-16 mx-auto mb-4 object-contain" />
          ) : (
            <Building2 size={36} className="mx-auto mb-4 text-gray-500" />
          )}
          <div className="w-12 h-12 mx-auto rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <Lock size={24} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">הגישה למערכת אינה פעילה</h2>
          <p className="text-sm text-gray-400 mb-1">{statusLabel}</p>
          <p className="text-sm text-gray-400 mb-5">
            המנוי של המשרד אינו פעיל כרגע. יש לפנות לבעל המשרד לצורך הפעלת המנוי מחדש.
          </p>
          {entitlement.office_name && (
            <div className="mb-4 text-xs text-gray-500 space-y-0.5">
              <p>משרד: <span className="text-gray-300">{entitlement.office_name}</span></p>
              <p>תפקיד: <span className="text-gray-300">{roleLabel}</span></p>
            </div>
          )}
          <button
            onClick={async () => {
              await signOut(auth);
              navigate('/login');
            }}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors text-sm font-semibold"
          >
            <LogOut size={15} />
            התנתקות
          </button>
        </div>
      </div>
    );
  }

  // 5. Has office + can access (or owner in expired state) → pass through
  return <>{children}</>;
}

/**
 * Expired-status banner for Owner.
 * Shows a non-blocking notice when the office is in an expired/past_due/cancelled state.
 */
export function OwnerExpiredBanner() {
  const { entitlement } = useEntitlement();

  if (!entitlement.office_id) return null;
  if (entitlement.my_role !== 'owner') return null;
  if (!entitlement.is_expired && !entitlement.is_trial_expired) return null;

  const statusLabel = EXPIRED_STATUS_LABELS[entitlement.subscription_status ?? ''] ?? 'המנוי אינו פעיל';

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2.5" dir="rtl">
      <div className="container mx-auto flex items-center gap-2 text-amber-300 text-sm">
        <AlertCircle size={16} className="shrink-0" />
        <span className="font-semibold">{statusLabel}</span>
        <span className="text-amber-300/70 hidden sm:inline">— ניתן לצפות במידע הקיים. להפעלה מלאה יש להפעיל מנוי.</span>
      </div>
    </div>
  );
}
